import { inject, Injectable, signal } from '@angular/core';
import { environment } from '@env';
import { type WS, wsBuilder } from '@shared/ws/ws';
import { AuthService } from '@tvd/data-access/auth';
import { TranslateService } from '@ngx-translate/core';
import type { Subscription } from 'rxjs';

// ============================================================================
// TYPES
// ============================================================================

export interface ConfirmationWidget {
  type: 'confirmation';
  text: string;
  items: Array<{ label: string; detail?: string }>;
  confirmLabel?: string;
  cancelLabel?: string;
}

export interface QuickReplyWidget {
  type: 'quick_reply';
  text: string;
  options: Array<{ label: string; value: string }>;
}

export interface SupportFallbackWidget {
  type: 'support_fallback';
  text: string;
  url: string;
}

export type ChatWidget =
  | ConfirmationWidget
  | QuickReplyWidget
  | SupportFallbackWidget;

export interface AiChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  widget?: ChatWidget;
}

export interface AiChatSession {
  id: string;
  title: string;
  messageCount?: number;
  updatedAt?: string;
}

export type AiChatWsMessage =
  | {
      type: 'message';
      role: 'user' | 'assistant';
      content: string;
      contentBlocks?: unknown[];
      widget?: ChatWidget;
      timestamp: string;
    }
  | { type: 'typing'; isTyping: boolean }
  | { type: 'error'; error: string }
  | { type: 'session_restore'; sessionId: string; messages: unknown[] }
  | { type: 'pong'; timestamp: string };

// ============================================================================
// SERVICE
// ============================================================================

@Injectable({ providedIn: 'root' })
export class AiChatService {
  readonly #auth = inject(AuthService);
  readonly #translate = inject(TranslateService);

  #ws?: WS<AiChatWsMessage>;
  #subscriptions: Subscription[] = [];

  // Reactive state
  readonly messages = signal<AiChatMessage[]>([]);
  readonly sessions = signal<AiChatSession[]>([]);
  readonly isConnected = signal(false);
  readonly isTyping = signal(false);
  readonly currentSession = signal<AiChatSession | null>(null);
  readonly error = signal<string | null>(null);

  // Track connection context for session switching
  #currentOrgId = '';
  #currentShopId = '';

  async createSession(orgId: string, shopId: string): Promise<AiChatSession> {
    const token = this.#auth.token();
    const response = await fetch(`${environment.apiUrl}/chat/sessions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ orgId, shopId }),
    });

    if (!response.ok) {
      throw new Error('Failed to create session');
    }

    const result = await response.json();
    return result.session;
  }

  async fetchSessions(): Promise<void> {
    const token = this.#auth.token();
    if (!token) return;

    try {
      const response = await fetch(`${environment.apiUrl}/chat/sessions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        this.sessions.set(result.sessions || []);
      }
    } catch {
      // Silently fail — sessions list is non-critical
    }
  }

  switchSession(session: AiChatSession): void {
    if (!this.#currentOrgId || !this.#currentShopId) return;
    this.messages.set([]);
    this.currentSession.set(session);
    this.connect(session.id, this.#currentOrgId, this.#currentShopId);
  }

  connect(sessionId: string, orgId: string, shopId: string): void {
    this.disconnect();
    this.#currentOrgId = orgId;
    this.#currentShopId = shopId;

    const token = this.#auth.token();
    if (!token) return;

    const url = new URL(`${environment.wsUrl}/chat/websocket`);
    url.searchParams.set('token', token);
    url.searchParams.set('sessionId', sessionId);
    url.searchParams.set('orgId', orgId);
    url.searchParams.set('shopId', shopId);
    url.searchParams.set('locale', this.#translate.currentLang || 'en');

    this.#ws = wsBuilder<AiChatWsMessage>(url.toString())
      .withRetry(5, 3000)
      .create();

    const statusSub = this.#ws.status$.subscribe((status) => {
      this.isConnected.set(status === 'open');
    });

    const msgSub = this.#ws.messages$.subscribe((msg) => {
      this.#handleMessage(msg);
    });

    this.#subscriptions = [statusSub, msgSub];
    this.#ws.connect();
  }

  send(text: string): void {
    if (!this.#ws) return;
    this.#ws.send({ type: 'message', text });

    const userMsg: AiChatMessage = {
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
    };
    this.messages.update((msgs) => [...msgs, userMsg]);

    // Refresh session list after first message to pick up auto-generated title
    if (this.messages().length === 1) {
      setTimeout(() => this.fetchSessions(), 1000);
    }
  }

  disconnect(): void {
    for (const sub of this.#subscriptions) {
      sub.unsubscribe();
    }
    this.#subscriptions = [];

    if (this.#ws) {
      this.#ws.close();
      this.#ws = undefined;
    }

    this.isConnected.set(false);
    this.isTyping.set(false);
  }

  reset(): void {
    this.disconnect();
    this.messages.set([]);
    this.currentSession.set(null);
    this.error.set(null);
  }

  #handleMessage(msg: AiChatWsMessage): void {
    switch (msg.type) {
      case 'message': {
        if (msg.role === 'assistant') {
          // Server-side widget takes priority, fallback to text-marker extraction
          const serverWidget = msg.widget || null;
          const extracted = !serverWidget
            ? this.#extractWidget(msg.content)
            : null;
          const aiMsg: AiChatMessage = {
            role: 'assistant',
            content: extracted ? extracted.cleanContent : msg.content,
            timestamp: msg.timestamp,
            widget: serverWidget || extracted?.widget,
          };
          this.messages.update((msgs) => [...msgs, aiMsg]);
          this.error.set(null);
        }
        break;
      }
      case 'typing':
        this.isTyping.set(msg.isTyping);
        break;
      case 'error':
        this.error.set(msg.error);
        this.isTyping.set(false);
        break;
      case 'session_restore': {
        // Replay stored messages from DO on reconnect
        const restored: AiChatMessage[] = [];
        for (const m of msg.messages || []) {
          const msgObj = m as { role?: string; content?: string | unknown[] };
          if (msgObj.role === 'user' && typeof msgObj.content === 'string') {
            restored.push({
              role: 'user',
              content: msgObj.content,
              timestamp: '',
            });
          } else if (msgObj.role === 'assistant') {
            // Extract text from content blocks
            const blocks = Array.isArray(msgObj.content) ? msgObj.content : [];
            const text = blocks
              .filter((b: any) => b.type === 'text')
              .map((b: any) => b.text)
              .join('\n');
            if (text) {
              restored.push({
                role: 'assistant',
                content: text,
                timestamp: '',
              });
            }
          }
        }
        if (restored.length > 0) {
          this.messages.set(restored);
        }
        break;
      }
      case 'pong':
        break;
    }
  }

  /**
   * Extract widget data from AI response if present.
   * Claude may include structured widget hints in its response text
   * using a simple marker format: [WIDGET:type]{json}
   */
  #extractWidget(
    content: string,
  ): { cleanContent: string; widget: ChatWidget } | null {
    const widgetMatch = content.match(
      /\[WIDGET:(confirmation|quick_reply)\]\s*(\{[\s\S]*?\})\s*$/,
    );
    if (!widgetMatch) return null;

    try {
      const widgetType = widgetMatch[1] as 'confirmation' | 'quick_reply';
      const widgetData = JSON.parse(widgetMatch[2]);
      const cleanContent = content.slice(0, widgetMatch.index).trim();

      if (widgetType === 'confirmation') {
        return {
          cleanContent,
          widget: {
            type: 'confirmation',
            text: widgetData.text || '',
            items: widgetData.items || [],
            confirmLabel: widgetData.confirmLabel,
            cancelLabel: widgetData.cancelLabel,
          },
        };
      }

      if (widgetType === 'quick_reply') {
        return {
          cleanContent,
          widget: {
            type: 'quick_reply',
            text: widgetData.text || '',
            options: widgetData.options || [],
          },
        };
      }
    } catch {
      // Invalid widget JSON — just render as plain text
    }

    return null;
  }
}
